import React, { useState, useEffect, useContext } from "react";
import { useAuth } from "react-oidc-context";
import { HotelContext } from "../../context/HotelContext";
import { getLocale, translate } from "../../utils/Translation";
import { useV2MonthYear } from "../../hooks/useV2MonthYear";
import YearNavigator from "./YearNavigator";
import { LoadingIndicator, ValueCard, CircularChartCard } from "../../components/v2/cards";
import { DonationByStayDurationChart, DonationDistributionChart } from "./RewardsDonationsComponents";
import "./Page.css";
import "./RewardsDonations.css";

const formatWaterValue = (value, locale) => {
  if (value === null || value === undefined) return "--";
  if (typeof value === "number") {
    return value.toLocaleString(locale);
  }
  return value;
};

const RewardsDonations = () => {
  const keycloak = useAuth();
  const { hId, lang } = useContext(HotelContext);
  const locale = getLocale(lang);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [donationsStats, setDonationsStats] = useState(null);

  const { month, year: selectedYear, setMonthYear } = useV2MonthYear();

  const defaultStats = {
    waterDonation: {
      total: 0,
      details: [
        { type: "GreenMiles", total: 0 },
        { type: "Lunishower", total: 0 },
      ],
    },
    peopleHelped: {
      total: 0,
      variation: 0,
    },
    actionDonUsage: {
      percentage: 0,
      variation: 0,
    },
    donationByStayDuration: [
      { duration: "1 jour", value: 0 },
      { duration: "2 jours", value: 0 },
      { duration: "3 jours", value: 0 },
      { duration: "4 jours ou +", value: 0 },
    ],
    donationDistribution: [],
  };

  const fetchDonationsStats = () => {
    if (!keycloak.isAuthenticated || !keycloak.user?.access_token || !hId) {
      setHasError(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    setHasError(false);

    const params = new URLSearchParams({
      lang,
    });
    if (selectedYear) params.append("year", selectedYear);

    const url = `/api/hotels/${hId}/donations/stats?${params.toString()}`;

    fetch(url, {
      method: "GET",
      headers: {
        Authorization: "Bearer " + keycloak.user.access_token,
        "x-front-token": "Bs#PSQyhy)!y9Ti{",
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((errData) => {
            throw new Error(errData.message || `HTTP error! status: ${response.status}`);
          });
        }
        return response.json();
      })
      .then((data) => {
        setDonationsStats(data);
        setHasError(false);
        setLoading(false);
      })
      .catch((error) => {
        console.error("RewardsDonations - Error fetching data:", error);
        setHasError(true);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (keycloak.isAuthenticated && keycloak.user && hId) {
      fetchDonationsStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak.isAuthenticated, hId, selectedYear, lang]);

  const handleYearChange = ({ year }) => {
    setMonthYear({ month, year });
  };

  const stats = donationsStats || defaultStats;

  const waterDonationData = stats.waterDonation;
  const peopleHelpedTotal = stats.peopleHelped.total;
  const peopleHelpedVariation = stats.peopleHelped.variation;
  const actionDonUsagePercentage = stats.actionDonUsage.percentage;
  const actionDonUsageVariation = stats.actionDonUsage.variation;
  const donationByStayDurationData = stats.donationByStayDuration;
  const donationDistributionData = stats.donationDistribution;

  return (
    <div className="v2-page">
      <div className="v2-page__header">
        <h1>{translate("v2_rewards_donations_title", lang)}</h1>
        <YearNavigator year={selectedYear} onChange={handleYearChange} />
      </div>
      {hasError && (
        <div style={{ padding: "16px", background: "#ffebee", color: "#c62828", borderRadius: "4px", marginBottom: "24px" }}>
          {translate("v2_rewards_donations_error", lang)}
        </div>
      )}
      <div className="rewards-donations__content">
        <div className="rewards-donations__top-cards">
          <div className="rewards-donations__stat-card">
            <CircularChartCard
              title={translate("v2_rewards_donations_water", lang)}
              subtitle={translate("v2_rewards_donations_water_sub", lang)}
              data={waterDonationData}
              colors={["#6C8EBF", "#1F3451"]}
              startWithMaxSegment={true}
              startRotation={-90}
              drawOrder="reverse"
              legendPosition="right"
              formatValue={(value) => formatWaterValue(value, locale)}
              showLegendPercentage={true}
              loading={loading}
              unit="L"
              centerTextColor="#1F3451"
              centerTextSize={1}
              centerTextWeight="600"
            />
          </div>
          <div className="rewards-donations__stat-card">
            <ValueCard
              title={translate("v2_rewards_donations_people", lang)}
              subtitle={translate("v2_rewards_donations_annual", lang)}
              value={peopleHelpedTotal}
              unit=""
              showUnit={false}
              variation={peopleHelpedVariation}
              variationLabel={translate("v2_vs_last_year", lang)}
              showVariationPercentage={false}
              loading={loading}
              valueColor="#2d4a75"
            />
          </div>
          <div className="rewards-donations__stat-card">
            <ValueCard
              title={translate("v2_rewards_donations_action", lang)}
              subtitle={translate("v2_rewards_donations_monthly_avg", lang)}
              value={actionDonUsagePercentage}
              unit="%"
              showUnit={true}
              variation={actionDonUsageVariation}
              variationLabel={translate("v2_vs_last_month", lang)}
              loading={loading}
              valueColor="#2d4a75"
            />
          </div>
        </div>

        <div className="rewards-donations__bottom-cards">
          <div className="rewards-donations__card rewards-donations__card-large">
            <div className="rewards-donations__card-header">
              <h3 className="rewards-donations__card-title">{translate("v2_rewards_donations_by_stay", lang)}</h3>
              <p className="rewards-donations__card-subtitle">{translate("v2_rewards_donations_annual_analysis", lang)}</p>
            </div>
            {loading ? (
              <LoadingIndicator />
            ) : (
              <DonationByStayDurationChart data={donationByStayDurationData} />
            )}
          </div>
          <div className="rewards-donations__card rewards-donations__card-large">
            <div className="rewards-donations__card-header">
              <h3 className="rewards-donations__card-title">{translate("v2_rewards_donations_distribution", lang)}</h3>
              <p className="rewards-donations__card-subtitle">{translate("v2_rewards_donations_annual_dist", lang)}</p>
            </div>
            {loading ? (
              <LoadingIndicator />
            ) : (
              <DonationDistributionChart data={donationDistributionData} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RewardsDonations;
